import { useEffect } from 'react';
import { graphql } from 'react-relay';
import { ConnectionHandler, RecordSourceSelectorProxy } from 'relay-runtime';

import { BookBookScrapPaginator_book$key } from '../../../relay/__generated__/BookBookScrapPaginator_book.graphql';
import FragmentPaginator, { FragmentPaginatorProps } from '../../core/FragmentPaginator';

const BookBookScrapPaginator_book = graphql`
  fragment BookBookScrapPaginator_book on Book
  @argumentDefinitions(first: { type: Int }, after: { type: String })
  @refetchable(queryName: "BookBookScrapPaginatorRefreshQuery") {
    id
    bookScraps(first: $first, after: $after) @connection(key: "BookBookScrapPaginator_book_bookScraps") {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          actions
          problemBookPage
          solutionBookPage
          problemNumber
        }
      }
      ...BookScrapConnectionDataTable_bookScrapConnection
    }
  }
`;

// 서버가 한 번에 주는 최대 개수
const MAX_PAGE_SIZE = 200;

/**
 * 교재별로 화면에 불러 둔 문제 수.
 * 스크랩하기·제출 등으로 목록을 처음부터 다시 받으면 한 페이지만 남는데, 이 수만큼 다시 채운다.
 * 페이지와 모달이 같은 목록을 보므로 컴포넌트 밖에서 같이 쓴다.
 */
const loadedCounts = new Map<string, number>();

const clearLoadedCount = (bookId: string) => loadedCounts.delete(bookId);

/** 목록을 다시 받지 않고 지운 문제만 빼서, 불러 둔 나머지를 그대로 둔다. */
const removeBookScrapsFromList = (
  store: RecordSourceSelectorProxy,
  bookId: string,
  bookScrapIds: readonly string[],
) => {
  const book = store.get(bookId);
  const connection = book && ConnectionHandler.getConnection(book, 'BookBookScrapPaginator_book_bookScraps');
  if (!connection) return;
  const loadedIds = new Set(
    (connection.getLinkedRecords('edges') || []).map((edge) => edge?.getLinkedRecord('node')?.getDataID()),
  );
  const removedIds = bookScrapIds.filter((id) => loadedIds.has(id));
  removedIds.forEach((id) => ConnectionHandler.deleteNode(connection, id));
  const totalCount = connection.getValue('totalCount');
  if (typeof totalCount === 'number') connection.setValue(totalCount - bookScrapIds.length, 'totalCount');
  // 지운 만큼 줄여 둬야 빈자리를 다시 채우지 않는다.
  loadedCounts.set(bookId, Math.max((loadedCounts.get(bookId) ?? 0) - removedIds.length, 0));
};

type LoadedCountKeeperProps = {
  bookId: string;
  loadedCount: number;
  hasNext: boolean;
  isLoadingNext: boolean;
  loadMore: (count: number) => void;
};

const LoadedCountKeeper = ({ bookId, loadedCount, hasNext, isLoadingNext, loadMore }: LoadedCountKeeperProps) => {
  useEffect(() => {
    const keepCount = loadedCounts.get(bookId) ?? 0;
    if (loadedCount >= keepCount) {
      loadedCounts.set(bookId, loadedCount);
    } else if (hasNext && !isLoadingNext) {
      loadMore(Math.min(keepCount - loadedCount, MAX_PAGE_SIZE));
    }
  }, [bookId, loadedCount, hasNext, isLoadingNext]);
  return null;
};

type Props = {
  /** 목록을 다시 받아도 불러 뒀던 수만큼 다시 채운다. 같은 목록을 여러 곳에서 보면 한 곳에서만 켠다. */
  keepLoadedCount?: boolean;
} & Omit<FragmentPaginatorProps<BookBookScrapPaginator_book$key>, 'fragment'>;

const BookBookScrapPaginator = ({ keepLoadedCount = false, children, ...props }: Props) => {
  return (
    <FragmentPaginator fragment={BookBookScrapPaginator_book} {...props}>
      {(data, helpers) => (
        <>
          {keepLoadedCount ? (
            <LoadedCountKeeper
              bookId={data.id}
              loadedCount={data.bookScraps.edges.length}
              hasNext={helpers.hasNext}
              isLoadingNext={helpers.isLoadingNext}
              loadMore={(count) => helpers.loadMore(count)}
            />
          ) : null}
          {children(data, helpers)}
        </>
      )}
    </FragmentPaginator>
  );
};

export default BookBookScrapPaginator;
export { clearLoadedCount, removeBookScrapsFromList };
